import { useEffect, useRef } from "react";
import { toast } from "sonner";

/**
 * Logs the user out after `idleMs` of no activity.
 * Activity = mouse, keyboard, touch, scroll, or a document visibility change.
 * A 30-second warning toast fires before the actual logout.
 */
export default function useIdleLogout(onLogout, idleMs = 15 * 60 * 1000) {
  const timerRef = useRef(null);
  const warnRef = useRef(null);

  useEffect(() => {
    if (!onLogout) return;

    const reset = () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (warnRef.current) clearTimeout(warnRef.current);
      warnRef.current = setTimeout(() => {
        toast.warning("You will be logged out in 30 seconds due to inactivity.");
      }, Math.max(0, idleMs - 30_000));
      timerRef.current = setTimeout(() => {
        toast.error("Signed out due to inactivity");
        onLogout();
      }, idleMs);
    };

    const events = ["mousemove", "mousedown", "keydown", "scroll", "touchstart", "visibilitychange"];
    events.forEach((e) => window.addEventListener(e, reset, { passive: true }));
    reset();

    return () => {
      events.forEach((e) => window.removeEventListener(e, reset));
      if (timerRef.current) clearTimeout(timerRef.current);
      if (warnRef.current) clearTimeout(warnRef.current);
    };
  }, [onLogout, idleMs]);
}
